import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown, ChevronRight, Archive, ArchiveRestore, X, Check, ListChecks, Lock, ArrowUpDown,
} from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import EmptyState from './EmptyState';
import { PriorityIcon, FechaText, AsignadosStack, AsignadoAvatar, HITO_COLOR_DEFAULT } from './utils/displays';
import { refTareaUrl, tareaPorRefUrl } from './utils/helpers';
import {
  CATEGORIA_ORDER, CATEGORIA_LABEL, CATEGORIA_COLOR,
  PRIORIDAD_ORDER, PRIORIDAD_LABEL,
} from './utils/constants';

// ----- Opciones de toolbar -----

const AGRUPAR_OPTIONS = [
  { key: 'estado',    label: 'Estado' },
  { key: 'asignado',  label: 'Asignado' },
  { key: 'prioridad', label: 'Prioridad' },
  { key: 'fecha',     label: 'Fecha' },
  { key: 'etiqueta',  label: 'Etiqueta' },
  { key: 'hito',      label: 'Hito' },
];
// Agrupar por proyecto solo tiene sentido en la vista general (cross-proyecto).
const AGRUPAR_PROYECTO = { key: 'proyecto', label: 'Proyecto' };
const AGRUPAR_LABEL = Object.fromEntries(
  [AGRUPAR_PROYECTO, ...AGRUPAR_OPTIONS].map((o) => [o.key, o.label])
);

const ORDENAR_OPTIONS = [
  { key: 'creacion',   label: 'Creación' },
  { key: 'prioridad',  label: 'Prioridad' },
  { key: 'fecha',      label: 'Fecha de vencimiento' },
  { key: 'titulo',     label: 'Título (A→Z)' },
];
const ORDENAR_LABEL = Object.fromEntries(ORDENAR_OPTIONS.map((o) => [o.key, o.label]));

// Dropdown compacto reutilizable para los selectores del toolbar (Agrupar /
// Ordenar). Usa click-outside (no onBlur+setTimeout) para teclado-friendly.
const Dropdown = ({ label, icon: Icon, value, valueLabel, open, setOpen, options, onSelect }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open, setOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
      >
        {Icon && <Icon size={11} />}
        {label}: <strong className="font-medium text-slate-700">{valueLabel}</strong>
        <ChevronDown size={11} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.1 }}
          className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 py-1 z-10"
        >
          {options.map((opt) => (
            <button
              key={opt.key}
              onClick={() => { onSelect(opt.key); setOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                value === opt.key ? 'bg-slate-50 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// ----- Subtareas inline (expandible) -----

// Cache module-level de subtareas por tareaId. Estrategia stale-while-revalidate:
// al expandir, usamos lo cacheado al instante y refrescamos en background.
const _subtareasCache = new Map();

const SubtareasInline = ({ tareaId, color, onChanged }) => {
  const cached = _subtareasCache.get(tareaId);
  const [subtareas, setSubtareas] = useState(cached ?? null);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let alive = true;
    const hit = _subtareasCache.get(tareaId);
    if (hit) {
      setSubtareas(hit);
      setLoading(false);
    } else {
      setLoading(true);
    }
    axiosInstance.get(`/api/tareas/${tareaId}`)
      .then(({ data }) => {
        if (!alive) return;
        const subs = data.subtareas || [];
        _subtareasCache.set(tareaId, subs);
        setSubtareas(subs);
      })
      .catch(() => { if (alive && !hit) setSubtareas([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tareaId]);

  const handleToggle = async (s) => {
    const next = (subtareas || []).map((x) => (x.id === s.id ? { ...x, completado: !x.completado } : x));
    setSubtareas(next);
    _subtareasCache.set(tareaId, next);
    try {
      await axiosInstance.patch(`/api/tareas/${tareaId}/subtareas/${s.id}`, {
        completado: !s.completado,
      });
      onChanged?.();
    } catch (err) {
      console.error('Error toggle subtarea:', err);
      const revert = (next || []).map((x) => (x.id === s.id ? { ...x, completado: s.completado } : x));
      setSubtareas(revert);
      _subtareasCache.set(tareaId, revert);
    }
  };

  if (loading) {
    return (
      <div className="pl-[180px] pr-4 py-2 text-[11px] text-slate-400 bg-slate-50/40 border-t border-slate-100">
        Cargando subtareas...
      </div>
    );
  }

  if (!subtareas || subtareas.length === 0) {
    return (
      <div className="pl-[180px] pr-4 py-2 text-[11px] text-slate-400 italic bg-slate-50/40 border-t border-slate-100">
        Sin subtareas.
      </div>
    );
  }

  return (
    <ul className="pl-[180px] pr-4 py-2 bg-slate-50/40 border-t border-slate-100 space-y-0.5">
      {subtareas.map((s) => (
        <li
          key={s.id}
          className="group flex items-center gap-2 px-2 py-1 -mx-2 rounded hover:bg-white/80 transition-colors"
        >
          <button
            onClick={(e) => { e.stopPropagation(); handleToggle(s); }}
            className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-all ${
              s.completado ? '' : 'border-slate-300 hover:border-cyan-400 bg-white'
            }`}
            style={s.completado ? { backgroundColor: color, borderColor: color } : {}}
          >
            {s.completado && (
              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
                <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          <span className={`text-xs leading-snug min-w-0 truncate ${s.completado ? 'text-slate-400 opacity-60' : 'text-slate-700'}`}>
            {s.texto}
          </span>
          <span className="flex-1" />
          {s.fecha_vencimiento && (
            <FechaText fecha={s.fecha_vencimiento} compact />
          )}
          {s.asignado && (
            <AsignadoAvatar nombre={s.asignado.nombre} color={color} size={16} />
          )}
        </li>
      ))}
    </ul>
  );
};

// ----- Fila individual -----

const TareaRow = ({ tarea, color, selected, onToggleSelect, onClick, expanded, onToggleExpand, onChanged, onUnarchive, hideEstadoChip = false }) => {
  const codigo = tarea.codigo || `?-${tarea.numero}`;
  const etiquetas = (tarea.etiquetas || []).slice(0, 3);
  const tieneSubtareas = (tarea.subtareas_total || 0) > 0;
  const hechas = tarea.subtareas_completadas || 0;
  const total = tarea.subtareas_total || 0;
  const isArchivada = !!tarea.archivado_en;

  return (
    <>
      <motion.div
        layout="position"
        initial={false}
        onClick={onClick}
        className={`group grid grid-cols-[14px_24px_18px_72px_minmax(0,1fr)_auto_auto_auto_auto] items-center gap-3 px-4 py-2.5 transition-colors cursor-pointer ${
          selected ? 'bg-cyan-50' : 'hover:bg-slate-50'
        } ${isArchivada ? 'opacity-70' : ''}`}
      >
        {/* Chevron expand */}
        {tieneSubtareas ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(tarea.id); }}
            className="flex items-center justify-center text-slate-300 hover:text-slate-700 transition-colors"
            title={`${hechas}/${total} subtareas`}
          >
            <motion.span
              animate={{ rotate: expanded ? 90 : 0 }}
              transition={{ duration: 0.15 }}
            >
              <ChevronRight size={10} strokeWidth={2.5} />
            </motion.span>
          </button>
        ) : <span />}

        {/* Checkbox */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleSelect(tarea.id); }}
          className={`flex items-center justify-center w-4 h-4 rounded border transition-all ${
            selected
              ? 'bg-cyan-500 border-cyan-500'
              : 'border-slate-300 hover:border-cyan-400 opacity-0 group-hover:opacity-100'
          }`}
        >
          {selected && (
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none">
              <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>

        {/* Prioridad */}
        <span className="flex items-center justify-center">
          <PriorityIcon prioridad={tarea.prioridad} />
        </span>

        {/* ID */}
        <span className="font-mono text-[10px] tracking-widest text-slate-400 uppercase">
          {codigo}
        </span>

        {/* Título + etiquetas inline + contador subtareas (toda la info "primaria" junta) */}
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-slate-800 truncate">{tarea.titulo}</span>
          {(tarea.esta_bloqueada || (tarea.bloqueadoras_pendientes?.length > 0)) && (
            <span
              title={`Bloqueada por: ${(tarea.bloqueadoras_pendientes || []).map((b) => b.codigo).join(', ')}`}
              className="inline-flex items-center gap-0.5 px-1 py-px rounded bg-rose-50 text-rose-600 text-[9px] font-semibold leading-none shrink-0"
            >
              <Lock size={9} strokeWidth={2.5} />
              Bloq.
            </span>
          )}
          {tieneSubtareas && (
            <span className="text-[10px] text-slate-400 tabular-nums flex-shrink-0">
              {hechas}/{total}
            </span>
          )}
          {etiquetas.length > 0 && (
            <span className="flex items-center gap-1 flex-shrink-0">
              {etiquetas.map((e) => (
                <span
                  key={e.id}
                  title={e.nombre}
                  className="inline-flex items-center gap-1 pl-1.5 pr-1.5 py-px rounded text-[9px] font-medium leading-none"
                  style={{
                    backgroundColor: `${e.color || '#94a3b8'}1a`,
                    color: e.color || '#475569',
                  }}
                >
                  <span className="w-1 h-1 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
                  {e.nombre}
                </span>
              ))}
            </span>
          )}
        </span>

        {/* Fecha */}
        <span className="min-w-[64px] text-right">
          <FechaText
            fecha={tarea.fecha_vencimiento}
            espera={tarea.congelada && !['hecho', 'cancelado'].includes(tarea.estado?.categoria)
              ? (tarea.bloqueadoras_pendientes || []).map((b) => b.codigo) : null}
          />
        </span>

        {/* Estado chip (oculto cuando ya agrupás por estado: la info se duplica con el header del panel) */}
        {hideEstadoChip ? <span /> : (
          <span
            className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap"
            style={{
              backgroundColor: `${tarea.estado?.color || '#94a3b8'}1f`,
              color: tarea.estado?.color || '#475569',
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tarea.estado?.color || '#94a3b8' }} />
            {tarea.estado?.nombre || '—'}
          </span>
        )}

        {/* Asignados */}
        <span className="flex items-center justify-end min-w-[24px]">
          {tarea.asignados?.length > 0 ? (
            <AsignadosStack asignados={tarea.asignados} color={color} size={20} max={3} />
          ) : (
            <span className="w-5 h-5 rounded-full border border-dashed border-slate-200" />
          )}
        </span>

        {/* Desarchivar (solo si la tarea está archivada y hay handler) */}
        {isArchivada && onUnarchive ? (
          <button
            onClick={(e) => { e.stopPropagation(); onUnarchive(tarea.id); }}
            title="Desarchivar"
            className="p-1 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-colors opacity-0 group-hover:opacity-100"
          >
            <ArchiveRestore size={12} />
          </button>
        ) : <span />}
      </motion.div>

      <AnimatePresence initial={false}>
        {expanded && tieneSubtareas && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <SubtareasInline tareaId={tarea.id} color={color} onChanged={onChanged} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ----- Vista lista -----

const ProyectoListaView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    proyecto,
    tareas,
    refetchTareas,
    refetchProyecto,
    updateTareaLocal,
    color,
    // Vista general: las tareas vienen de todos los proyectos.
    esVistaGeneral = false,
    proyectosMap,
    resolverEstadoId,
  } = useOutletContext();

  const opcionesAgrupar = esVistaGeneral
    ? [AGRUPAR_PROYECTO, ...AGRUPAR_OPTIONS]
    : AGRUPAR_OPTIONS;

  // En la vista general arranca agrupada por proyecto: es la lectura que se
  // espera al abrir "todas las tareas" de golpe.
  const [agrupar, setAgrupar] = useState(esVistaGeneral ? 'proyecto' : 'estado');
  const [agruparOpen, setAgruparOpen] = useState(false);
  // Ordenamiento dentro de cada grupo. Default 'creacion' = orden de creacion
  // (estable, conocido). 'prioridad' usa el orden semántico de PRIORIDAD_ORDER;
  // 'fecha' usa fecha_vencimiento (sin fecha al final); 'titulo' alfabético.
  const [ordenar, setOrdenar] = useState('creacion');
  const [ordenarOpen, setOrdenarOpen] = useState(false);
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [collapsed, setCollapsed] = useState({});
  const [expanded, setExpanded] = useState(new Set()); // Set<tarea_id> expandidos
  const [slideOverTareaId, setSlideOverTareaId] = useState(null);
  // Vista "archivadas": fetch independiente del shell, se carga solo cuando el toggle
  // se activa. Cuando está activo, la lista reemplaza las tareas activas (no las mezcla).
  const [mostrarArchivadas, setMostrarArchivadas] = useState(false);
  const [tareasArchivadas, setTareasArchivadas] = useState([]);
  const [loadingArchivadas, setLoadingArchivadas] = useState(false);

  const fetchArchivadas = useCallback(async () => {
    if (!esVistaGeneral && !proyecto?.id) return;
    setLoadingArchivadas(true);
    try {
      // Vista general: archivadas de todos los proyectos; proyecto: solo las suyas.
      // Ojo: /api/tareas?archivados=true levanta el filtro (devuelve activas +
      // archivadas), asi que aca nos quedamos solo con las archivadas.
      const { data } = esVistaGeneral
        ? await axiosInstance.get('/api/tareas', { params: { archivados: true } })
        : await axiosInstance.get(
            `/api/proyectos/${proyecto.id}/tareas`,
            { params: { archivadas: true } }
          );
      const lista = data || [];
      setTareasArchivadas(esVistaGeneral ? lista.filter((t) => t.archivado_en) : lista);
    } catch (err) {
      console.error('Error cargando archivadas:', err);
    } finally {
      setLoadingArchivadas(false);
    }
  }, [proyecto?.id, esVistaGeneral]);

  useEffect(() => {
    if (mostrarArchivadas) fetchArchivadas();
  }, [mostrarArchivadas, fetchArchivadas]);

  const handleDesarchivar = useCallback(async (tareaId) => {
    try {
      await axiosInstance.post(`/api/tareas/${tareaId}/desarchivar`);
      setTareasArchivadas((prev) => prev.filter((t) => t.id !== tareaId));
      refetchTareas?.();
    } catch (err) {
      console.error('Error desarchivando:', err);
    }
  }, [refetchTareas]);

  // Las tareas que se muestran: archivadas (si toggle activo) o activas (del context).
  const tareasMostradas = mostrarArchivadas ? tareasArchivadas : tareas;

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Detectar /tareas/:numero en URL para abrir slide-over. location.pathname
  // como dep asegura sincronización con back/forward del navegador.
  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/[^/]+\/lista\/tareas\/(\d+)/);
    if (match) {
      const ref = parseInt(match[1], 10);
      const tarea = tareaPorRefUrl(tareas, ref, esVistaGeneral);
      if (tarea) setSlideOverTareaId(tarea.id);
    } else {
      setSlideOverTareaId(null);
    }
  }, [tareas, location.pathname, esVistaGeneral]);

  const toggleSelect = useCallback((id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = () => setSeleccionados(new Set());

  // ----- Agrupacion -----

  const grupos = useMemo(() => {
    const groups = new Map();
    const getKey = (t) => {
      if (agrupar === 'proyecto') return `proy-${t.proyecto_id}`;
      if (agrupar === 'estado') return t.estado?.categoria || 'sin';
      if (agrupar === 'asignado') return t.asignados?.length ? `asig-${t.asignados[0].id}` : 'sin';
      if (agrupar === 'prioridad') return t.prioridad || 'ninguna';
      if (agrupar === 'fecha') {
        if (!t.fecha_vencimiento) return 'sin';
        const today = new Date(); today.setHours(0,0,0,0);
        const d = new Date(t.fecha_vencimiento + 'T00:00:00');
        const diff = Math.floor((d - today) / 86400000);
        if (diff < 0) return 'vencidas';
        if (diff === 0) return 'hoy';
        if (diff <= 7) return 'semana';
        return 'proximas';
      }
      if (agrupar === 'etiqueta') {
        if (!t.etiquetas?.length) return 'sin';
        return `etiq-${t.etiquetas[0].id}`;
      }
      if (agrupar === 'hito') {
        return t.hito?.id ? `hito-${t.hito.id}` : 'sin';
      }
      return 'sin';
    };

    // Comparador del ordenar-dentro-de-grupo. Ninguno garantiza estabilidad
    // perfecta pero usa created_at como tiebreaker para que el resultado no
    // baile entre renders.
    const cmp = (a, b) => {
      const tiebreak = new Date(a.created_at) - new Date(b.created_at);
      if (ordenar === 'prioridad') {
        const oa = PRIORIDAD_ORDER[a.prioridad || 'ninguna'] ?? 99;
        const ob = PRIORIDAD_ORDER[b.prioridad || 'ninguna'] ?? 99;
        if (oa !== ob) return oa - ob;
        return tiebreak;
      }
      if (ordenar === 'fecha') {
        if (!a.fecha_vencimiento && !b.fecha_vencimiento) return tiebreak;
        if (!a.fecha_vencimiento) return 1;
        if (!b.fecha_vencimiento) return -1;
        const dif = a.fecha_vencimiento.localeCompare(b.fecha_vencimiento);
        return dif !== 0 ? dif : tiebreak;
      }
      if (ordenar === 'titulo') {
        return a.titulo.localeCompare(b.titulo, 'es', { sensitivity: 'base' });
      }
      return tiebreak; // 'creacion'
    };

    (tareasMostradas || []).forEach((t) => {
      const k = getKey(t);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(t);
    });

    // Aplicar sort dentro de cada grupo antes de exponer.
    groups.forEach((items) => items.sort(cmp));

    // Construir array de grupos con labels
    const result = [];
    if (agrupar === 'proyecto') {
      Array.from(groups.keys()).forEach((k) => {
        const primera = groups.get(k)[0];
        const p = proyectosMap?.[primera?.proyecto_id];
        result.push({
          key: k,
          label: p?.nombre || primera?.proyecto_nombre || 'Proyecto',
          items: groups.get(k),
          color: p?.color || primera?.proyecto_color,
        });
      });
      result.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
    } else if (agrupar === 'estado') {
      CATEGORIA_ORDER.forEach((cat) => {
        if (groups.has(cat)) {
          result.push({ key: cat, label: CATEGORIA_LABEL[cat], items: groups.get(cat), color: CATEGORIA_COLOR[cat] });
        }
      });
    } else if (agrupar === 'prioridad') {
      Object.keys(PRIORIDAD_ORDER)
        .sort((a, b) => PRIORIDAD_ORDER[a] - PRIORIDAD_ORDER[b])
        .forEach((p) => {
          if (groups.has(p)) {
            result.push({ key: p, label: PRIORIDAD_LABEL[p], items: groups.get(p) });
          }
        });
    } else if (agrupar === 'fecha') {
      [
        { k: 'vencidas', label: 'Vencidas', color: '#ef4444' },
        { k: 'hoy', label: 'Hoy', color: '#f59e0b' },
        { k: 'semana', label: 'Esta semana', color: '#06b6d4' },
        { k: 'proximas', label: 'Próximas', color: '#64748b' },
        { k: 'sin', label: 'Sin fecha', color: '#94a3b8' },
      ].forEach(({ k, label, color: c }) => {
        if (groups.has(k)) result.push({ key: k, label, items: groups.get(k), color: c });
      });
    } else if (agrupar === 'asignado') {
      const empleadoMap = {};
      (proyecto?.miembros || []).forEach((m) => { empleadoMap[`asig-${m.empleado_id}`] = m.nombre; });
      Array.from(groups.keys()).forEach((k) => {
        const label = k === 'sin' ? 'Sin asignar' : (empleadoMap[k] || 'Otro');
        result.push({ key: k, label, items: groups.get(k) });
      });
      result.sort((a, b) => (a.key === 'sin' ? 1 : b.key === 'sin' ? -1 : a.label.localeCompare(b.label)));
    } else if (agrupar === 'etiqueta') {
      const etiquetaMap = {};
      (proyecto?.etiquetas || []).forEach((e) => { etiquetaMap[`etiq-${e.id}`] = e; });
      Array.from(groups.keys()).forEach((k) => {
        if (k === 'sin') {
          result.push({ key: k, label: 'Sin etiqueta', items: groups.get(k) });
        } else {
          const et = etiquetaMap[k];
          result.push({ key: k, label: et?.nombre || 'Etiqueta', items: groups.get(k), color: et?.color });
        }
      });
    } else if (agrupar === 'hito') {
      const hitoMap = {};
      (proyecto?.hitos || []).forEach((h) => { hitoMap[`hito-${h.id}`] = h; });
      Array.from(groups.keys()).forEach((k) => {
        if (k === 'sin') {
          result.push({ key: k, label: 'Sin hito', items: groups.get(k) });
        } else {
          const hi = hitoMap[k];
          result.push({ key: k, label: hi?.nombre || 'Hito', items: groups.get(k), color: hi?.color || HITO_COLOR_DEFAULT });
        }
      });
      result.sort((a, b) => (a.key === 'sin' ? 1 : b.key === 'sin' ? -1 : a.label.localeCompare(b.label)));
    }
    return result;
  }, [tareasMostradas, agrupar, ordenar, proyecto, proyectosMap]);

  // ----- Acciones bulk -----

  const handleBulkArchivar = async () => {
    const ids = Array.from(seleccionados);
    await Promise.all(ids.map((id) => axiosInstance.post(`/api/tareas/${id}/archivar`)));
    clearSelection();
    refetchTareas();
  };

  const handleBulkEstado = async (estadoId) => {
    const ids = Array.from(seleccionados);
    // Vista general: el menu lista categorias (estados virtuales `cat:x`) y cada
    // tarea se manda al estado equivalente de su propio proyecto.
    const destinoDe = (id) => {
      if (!esVistaGeneral) return estadoId;
      const t = (tareasMostradas || []).find((x) => x.id === id);
      return resolverEstadoId?.(t, String(estadoId).replace('cat:', ''))?.id || null;
    };
    await Promise.all(
      ids.map((id) => {
        const destino = destinoDe(id);
        return destino ? axiosInstance.put(`/api/tareas/${id}`, { estado_id: destino }) : null;
      }).filter(Boolean)
    );
    clearSelection();
    refetchTareas();
  };

  const allInGroupSelected = (items) => items.every((t) => seleccionados.has(t.id));
  const toggleGroupSelection = (items) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      const allSelected = items.every((t) => next.has(t.id));
      if (allSelected) items.forEach((t) => next.delete(t.id));
      else items.forEach((t) => next.add(t.id));
      return next;
    });
  };

  // ----- Slide over -----

  const handleRowClick = useCallback((tarea) => {
    setSlideOverTareaId(tarea.id);
    navigate(`/proyectos/${slug}/lista/tareas/${refTareaUrl(tarea, esVistaGeneral)}`, { replace: false });
  }, [navigate, slug, esVistaGeneral]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    navigate(`/proyectos/${slug}/lista`, { replace: false });
  }, [navigate, slug]);

  // Orden visual: items planos en el orden de los grupos
  const tareasOrdenadas = useMemo(
    () => grupos.flatMap((g) => g.items),
    [grupos]
  );

  // El slide-over necesita la config del proyecto REAL de la tarea (estados,
  // etiquetas, miembros, hitos); en la vista general el del contexto es sintetico.
  const proyectoDelSlideOver = useMemo(() => {
    if (!esVistaGeneral || !slideOverTareaId) return proyecto;
    const t = (tareasMostradas || []).find((x) => x.id === slideOverTareaId);
    return (t && proyectosMap?.[t.proyecto_id]) || proyecto;
  }, [esVistaGeneral, slideOverTareaId, tareasMostradas, proyectosMap, proyecto]);

  const handleNavigateSlideOver = useCallback((dir) => {
    if (!slideOverTareaId) return;
    const idx = tareasOrdenadas.findIndex((t) => t.id === slideOverTareaId);
    if (idx === -1) return;
    const nextIdx = dir === 'next' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= tareasOrdenadas.length) return;
    const next = tareasOrdenadas[nextIdx];
    setSlideOverTareaId(next.id);
    navigate(`/proyectos/${slug}/lista/tareas/${refTareaUrl(next, esVistaGeneral)}`, { replace: false });
  }, [slideOverTareaId, tareasOrdenadas, navigate, slug, esVistaGeneral]);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-slate-200/60">
        <div className="flex items-center gap-2">
          {seleccionados.size > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 px-3 py-1 bg-slate-900 text-white rounded-md text-xs"
            >
              <span className="tabular-nums">{seleccionados.size} seleccionada{seleccionados.size > 1 ? 's' : ''}</span>
              <div className="h-3 w-px bg-white/30" />
              <button
                onClick={handleBulkArchivar}
                className="inline-flex items-center gap-1 px-1 hover:text-rose-300 transition-colors"
              >
                <Archive size={11} /> Archivar
              </button>
              <BulkEstadoMenu
                estados={proyecto?.estados || []}
                onSelect={handleBulkEstado}
              />
              <button
                onClick={clearSelection}
                className="inline-flex items-center gap-1 hover:text-white/70 transition-colors"
                title="Limpiar selección"
              >
                <X size={12} />
              </button>
            </motion.div>
          ) : (
            <span className="text-xs text-slate-400 tabular-nums">
              {tareasMostradas.length} {tareasMostradas.length === 1 ? (mostrarArchivadas ? 'archivada' : 'tarea') : (mostrarArchivadas ? 'archivadas' : 'tareas')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Toggle: ver archivadas */}
          <button
            onClick={() => setMostrarArchivadas((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
              mostrarArchivadas
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
            title={mostrarArchivadas ? 'Volver a tareas activas' : 'Ver tareas archivadas'}
          >
            <Archive size={11} />
            {mostrarArchivadas ? 'Salir de archivadas' : 'Ver archivadas'}
          </button>

          {/* Agrupar */}
          <Dropdown
            label="Agrupar"
            value={agrupar}
            valueLabel={AGRUPAR_LABEL[agrupar]}
            open={agruparOpen}
            setOpen={setAgruparOpen}
            options={opcionesAgrupar}
            onSelect={setAgrupar}
          />

          {/* Ordenar dentro del grupo */}
          <Dropdown
            label="Ordenar"
            icon={ArrowUpDown}
            value={ordenar}
            valueLabel={ORDENAR_LABEL[ordenar]}
            open={ordenarOpen}
            setOpen={setOrdenarOpen}
            options={ORDENAR_OPTIONS}
            onSelect={setOrdenar}
          />
        </div>
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto px-6 py-3">
        {mostrarArchivadas && loadingArchivadas ? (
          <div className="flex items-center justify-center py-24">
            <div className="text-xs text-slate-400">Cargando archivadas...</div>
          </div>
        ) : tareasMostradas.length === 0 ? (
          <div className="py-24 px-6">
            <EmptyState
              icon={mostrarArchivadas ? Archive : ListChecks}
              title={mostrarArchivadas
                ? 'Sin tareas archivadas'
                : esVistaGeneral ? 'Todavía no hay tareas' : 'Este proyecto no tiene tareas'}
              description={mostrarArchivadas
                ? 'Las tareas que archives van a aparecer aquí.'
                : esVistaGeneral
                ? 'Entra a un proyecto y agrega la primera al pie de cualquier columna.'
                : 'Vuelve al board y agrega tu primera al pie de cualquier columna.'}
              variant="centered"
            />
          </div>
        ) : (
          <div className="space-y-4">
            {grupos.map((g) => {
              const isCollapsed = collapsed[g.key];
              const allSelected = g.items.length > 0 && allInGroupSelected(g.items);
              return (
                <section key={g.key} className="bg-white border border-slate-200/70 rounded-xl overflow-hidden">
                  {/* Header del panel: dot + label + count + selector */}
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50/40 border-b border-slate-100">
                    <button
                      onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !c[g.key] }))}
                      className="flex items-center gap-2 group"
                    >
                      <motion.span
                        animate={{ rotate: isCollapsed ? 0 : 90 }}
                        transition={{ duration: 0.15 }}
                        className="text-slate-300 group-hover:text-slate-500"
                      >
                        <ChevronRight size={11} strokeWidth={2.5} />
                      </motion.span>
                      {g.color && (
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: g.color }} />
                      )}
                      <span className="text-[11px] font-bold tracking-[0.1em] uppercase text-slate-600">
                        {g.label}
                      </span>
                      <span className="text-[10px] font-medium text-slate-400 tabular-nums">
                        {g.items.length}
                      </span>
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleGroupSelection(g.items); }}
                      className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                        allSelected
                          ? 'text-cyan-600 bg-cyan-50 hover:bg-cyan-100'
                          : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      {allSelected ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    </button>
                  </div>

                  <AnimatePresence initial={false}>
                    {!isCollapsed && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="divide-y divide-slate-100"
                      >
                        {g.items.map((tarea) => (
                          <TareaRow
                            key={tarea.id}
                            tarea={tarea}
                            // En la vista general cada fila usa el color de su
                            // propio proyecto; dentro de un proyecto es el mismo.
                            color={tarea.proyecto_color || color}
                            selected={seleccionados.has(tarea.id)}
                            onToggleSelect={toggleSelect}
                            onClick={() => handleRowClick(tarea)}
                            expanded={expanded.has(tarea.id)}
                            onToggleExpand={toggleExpand}
                            onChanged={refetchTareas}
                            hideEstadoChip={agrupar === 'estado'}
                            onUnarchive={mostrarArchivadas ? handleDesarchivar : undefined}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </section>
              );
            })}
          </div>
        )}
      </div>

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={proyectoDelSlideOver}
        onProyectoChanged={refetchProyecto}
        onClose={handleCloseSlideOver}
        onUpdate={(tareaCompleta) => updateTareaLocal?.(tareaCompleta.id, tareaCompleta)}
        onArchive={() => {
          refetchTareas();
          handleCloseSlideOver();
        }}
        onDuplicate={(nueva) => {
          refetchTareas();
          setSlideOverTareaId(nueva.id);
        }}
        onNavigate={handleNavigateSlideOver}
      />
    </div>
  );
};

// ----- Bulk estado menu -----

const BulkEstadoMenu = ({ estados, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-1 hover:text-cyan-300 transition-colors"
      >
        <Check size={11} /> Mover a...
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-xl ring-1 ring-slate-200 py-1 z-30 max-h-72 overflow-y-auto">
          {estados.map((e) => (
            <button
              key={e.id}
              onClick={() => { onSelect(e.id); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color || '#94a3b8' }} />
              {e.nombre}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProyectoListaView;
